import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Lock, Star, Trophy, Crown } from 'lucide-react';
import { AppState } from '../../app/types';
import { GroupId } from '../../config/systemConfig';
import { MODULES } from '../../content';
import { useAppStore } from '../../app/hooks/useAppStore';
import { useDjaySelection, DjayAvatar } from '../../mascot';
import type { DjayVariant } from '../../mascot';
import { dataLayer } from '../../data/dataLayer';
import { PageHero } from '../shared/PageHero';
import { PageContainer } from '../shared/PageContainer';
import { itemVariants } from '../shared/animations';
import { ACHIEVEMENTS, AchievementCheckData, RARITY_CONFIG, CATEGORY_LABELS, AchievementCard, CollectibleAchievementCard, Achievement } from '../progress/AchievementsSection';
import { getAchievementIllustration } from '../../mascot/illustrations';
interface CollectionPageProps {
  state: AppState;
  selectedGroup: GroupId;
}
type MascotRarity = 'common' | 'uncommon' | 'rare' | 'legendary' | 'secret';
type CategoryTab = Achievement['category'] | 'all';
const RARITY_STYLES: Record<MascotRarity, {
  border: string;
  bg: string;
  text: string;
  glow: string;
  label: string;
}> = {
  common: {
    border: 'border-slate-200',
    bg: 'bg-slate-50',
    text: 'text-slate-600',
    glow: '',
    label: 'Gewoon'
  },
  uncommon: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    glow: 'shadow-[0_0_15px_rgba(52,211,153,0.3)]',
    label: 'Ongewoon'
  },
  rare: {
    border: 'border-amber-300',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    glow: 'shadow-[0_0_15px_rgba(251,191,36,0.4)]',
    label: 'Zeldzaam'
  },
  legendary: {
    border: 'border-purple-400',
    bg: 'bg-purple-50',
    text: 'text-purple-700',
    glow: 'shadow-[0_0_20px_rgba(167,139,250,0.5)]',
    label: 'Legendarisch'
  },
  secret: {
    border: 'border-rose-400',
    bg: 'bg-rose-50',
    text: 'text-rose-700',
    glow: 'shadow-[0_0_20px_rgba(244,63,94,0.5)]',
    label: 'Geheim'
  }
};
export function CollectionPage({
  state,
  selectedGroup
}: CollectionPageProps) {
  const {
    getModuleMasteryForGroup,
    getAnsweredQuestions,
    accounts
  } = useAppStore();
  const {
    selectedVariant,
    setSelectedVariant
  } = useDjaySelection();
  const activeAccountId = accounts?.activeAccountId;
  const [hasDiscoveredLongPress, setHasDiscoveredLongPress] = useState(false);
  const [activeTab, setActiveTab] = useState<CategoryTab>('all');
  useEffect(() => {
    if (activeAccountId) {
      dataLayer.getDjayLongPressDiscovered(activeAccountId).then(setHasDiscoveredLongPress);
    }
  }, [activeAccountId]);
  // --- Mascot Variants Logic ---
  const totalQuestionsAnswered = useMemo(() => {
    return MODULES.reduce((total, module) => total + getAnsweredQuestions(module.id).length, 0);
  }, [getAnsweredQuestions]);
  const masteredCount = useMemo(() => {
    let mastered = 0;
    MODULES.forEach(m => {
      const mastery = getModuleMasteryForGroup(m.id);
      if (mastery >= 85) mastered++;
    });
    return mastered;
  }, [getModuleMasteryForGroup]);
  const consecutiveFullWeeks = state.dailyStreak?.consecutiveFullWeeks || 0;
  const mascotCards = useMemo(() => {
    return [{
      variant: 'default' as DjayVariant,
      name: 'Standaard Djay',
      description: 'Altijd beschikbaar',
      unlocked: true,
      rarity: 'common' as MascotRarity,
      progress: {
        current: 1,
        max: 1,
        label: 'Ontgrendeld'
      }
    }, {
      variant: 'pixel' as DjayVariant,
      name: 'DJ Pixel',
      description: 'Honderdtal bereikt',
      unlocked: totalQuestionsAnswered >= 100,
      rarity: 'uncommon' as MascotRarity,
      progress: {
        current: Math.min(totalQuestionsAnswered, 100),
        max: 100,
        label: 'vragen'
      }
    }, {
      variant: 'blaze' as DjayVariant,
      name: 'DJ Blaze',
      description: '3 volle weken achter elkaar',
      unlocked: consecutiveFullWeeks >= 3,
      rarity: 'rare' as MascotRarity,
      progress: {
        current: Math.min(consecutiveFullWeeks, 3),
        max: 3,
        label: 'weken'
      }
    }, {
      variant: 'frost' as DjayVariant,
      name: 'DJ Frost',
      description: '3 vakken afgerond',
      unlocked: masteredCount >= 3,
      rarity: 'rare' as MascotRarity,
      progress: {
        current: Math.min(masteredCount, 3),
        max: 3,
        label: 'vakken'
      }
    }, {
      variant: 'royal' as DjayVariant,
      name: 'DJ Royal',
      description: 'Alle vakken afgerond',
      unlocked: masteredCount >= MODULES.length,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(masteredCount, MODULES.length),
        max: MODULES.length,
        label: 'vakken'
      }
    }, {
      variant: 'cosmic' as DjayVariant,
      name: 'DJ Cosmic',
      description: 'Level 15 bereikt',
      unlocked: state.stats.level >= 15,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(state.stats.level, 15),
        max: 15,
        label: 'levels'
      }
    }, {
      variant: 'shadow' as DjayVariant,
      name: 'DJ Shadow',
      description: '30.000 XP behaald',
      unlocked: state.stats.xp >= 30000,
      rarity: 'legendary' as MascotRarity,
      progress: {
        current: Math.min(state.stats.xp, 30000),
        max: 30000,
        label: 'XP'
      }
    }, {
      variant: 'goud' as DjayVariant,
      name: hasDiscoveredLongPress ? 'DJ Goud' : '???',
      description: hasDiscoveredLongPress ? 'Geheim ontdekt!' : 'Geheim! Ontdek het zelf...',
      unlocked: hasDiscoveredLongPress,
      rarity: 'secret' as MascotRarity,
      progress: {
        current: hasDiscoveredLongPress ? 1 : 0,
        max: 1,
        label: 'Geheim'
      }
    }];
  }, [totalQuestionsAnswered, consecutiveFullWeeks, masteredCount, state.stats.level, state.stats.xp, hasDiscoveredLongPress]);
  const unlockedVariantsCount = mascotCards.filter(c => c.unlocked).length;
  // --- Achievements Logic ---
  const currentWeekDays = state.dailyStreak?.currentWeekDays || 0;
  const longestTotalStreak = state.streak?.longestTotalStreak || 0;
  const totalDailyGoalsReached = state.dailyStreak?.totalDailyGoalsReached ?? (state.dailyStreak?.weeklyHistory ? Object.values(state.dailyStreak.weeklyHistory).reduce((a, b) => a + b, 0) : 0);
  const totalPerfectSessions = state.dailyStreak?.totalPerfectSessions ?? 0;
  const checkData: AchievementCheckData = useMemo(() => ({
    totalQuestionsAnswered,
    masteredCount,
    level: state.stats.level,
    xp: state.stats.xp,
    currentWeekDays,
    consecutiveFullWeeks,
    longestTotalStreak,
    totalDailyGoalsReached,
    totalPerfectSessions
  }), [totalQuestionsAnswered, masteredCount, state.stats.level, state.stats.xp, currentWeekDays, consecutiveFullWeeks, longestTotalStreak, totalDailyGoalsReached, totalPerfectSessions]);
  const achievementsWithProgress = useMemo(() => {
    return ACHIEVEMENTS.map(achievement => ({
      ...achievement,
      isUnlocked: achievement.check(checkData),
      progress: achievement.getProgress ? achievement.getProgress(checkData) : undefined
    }));
  }, [checkData]);
  const unlockedAchievementsCount = achievementsWithProgress.filter(a => a.isUnlocked).length;
  const totalAchievementsCount = achievementsWithProgress.length;
  const filteredAchievements = useMemo(() => {
    if (activeTab === 'all') return achievementsWithProgress;
    return achievementsWithProgress.filter(a => a.category === activeTab);
  }, [achievementsWithProgress, activeTab]);
  // Combined Progress
  const totalCollectibles = mascotCards.length + totalAchievementsCount;
  const totalUnlocked = unlockedVariantsCount + unlockedAchievementsCount;
  const progressPercentage = Math.round(totalUnlocked / totalCollectibles * 100);
  return <PageContainer className="pb-20" data-id="element-1165">
      <motion.div variants={itemVariants} data-id="element-1166">
        <PageHero title="Mijn Collectie" subtitle="Verdien prestaties door te oefenen en speel unieke Djay-looks vrij!" gradientClasses="from-amber-500 via-orange-500 to-rose-500" djayEmotion="excited" djayVariant={selectedVariant} icon={Sparkles} iconBgClass="bg-amber-400" floatingEmojis={['✨', '🏆', '⭐']} data-id="element-1167" />
      </motion.div>

      <motion.div variants={itemVariants} className="mt-8 space-y-8" data-id="element-1168">
        {/* Overall Progress Card */}
        <div className="bg-white p-5 sm:p-6 rounded-2xl border-2 border-slate-100 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6" data-id="element-1169">
          <div className="flex items-center gap-4" data-id="element-1170">
            <div className="relative w-16 h-16 flex-shrink-0" data-id="element-1171">
              <svg className="w-16 h-16 transform -rotate-90" data-id="element-1172">
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F1F5F9" strokeWidth="6" data-id="element-1173" />
                <circle cx="32" cy="32" r="28" fill="none" stroke="#F59E0B" strokeWidth="6" strokeDasharray="175.9" strokeDashoffset={175.9 - progressPercentage / 100 * 175.9} strokeLinecap="round" className="transition-all duration-1000 ease-out" data-id="element-1174" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center" data-id="element-1175">
                <Crown className="w-6 h-6 text-amber-500" data-id="element-1176" />
              </div>
            </div>
            <div data-id="element-1177">
              <h2 className="text-xl font-black text-slate-800" data-id="element-1178">
                Mijn Collectie
              </h2>
              <p className="text-sm text-slate-500 font-medium" data-id="element-1179">
                {totalUnlocked} van de {totalCollectibles} behaald
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3" data-id="element-1180">
            <div className="flex flex-col items-center sm:items-end" data-id="element-1181">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1182">
                Prestaties
              </span>
              <span className="text-lg font-black text-amber-500" data-id="element-1183">
                {unlockedAchievementsCount}/{totalAchievementsCount}
              </span>
            </div>
            <div className="w-px bg-slate-200 hidden sm:block" data-id="element-1184"></div>
            <div className="flex flex-col items-center sm:items-end" data-id="element-1185">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-1186">
                Varianten
              </span>
              <span className="text-lg font-black text-orange-500" data-id="element-1187">
                {unlockedVariantsCount}/{mascotCards.length}
              </span>
            </div>
          </div>
        </div>

        {/* Jouw Djay Section (Compact Variant Selector) */}
        <div className="space-y-4" data-id="element-1188">
          <div className="flex items-center justify-between px-1" data-id="element-1189">
            <h3 className="text-xl font-black text-slate-800 flex items-center gap-2" data-id="element-1190">
              <Star className="w-6 h-6 text-orange-500" data-id="element-1191" /> Jouw Djay
            </h3>
            <span className="text-sm font-bold text-slate-500" data-id="element-1192">
              Kies je look
            </span>
          </div>

          <div className="bg-white p-5 sm:p-6 rounded-2xl border-2 border-slate-100 shadow-sm flex flex-col sm:flex-row items-center gap-6 sm:gap-8" data-id="element-1193">
            {/* Left: Active Avatar */}
            <div className="relative w-32 h-32 flex-shrink-0 flex items-center justify-center bg-orange-50 rounded-full border-4 border-orange-100 shadow-inner" data-id="element-1194">
              <div className="transform scale-125 mt-4" data-id="element-1195">
                <DjayAvatar size="xl" emotion="excited" variant={selectedVariant} animate={true} data-id="element-1196" />
              </div>
            </div>

            {/* Right: Thumbnails */}
            <div className="flex-1 w-full" data-id="element-1197">
              <div className="flex flex-wrap justify-center sm:justify-start gap-3 mb-3" data-id="element-1198">
                {mascotCards.map(card => {
                const isSelected = selectedVariant === card.variant;
                const styles = RARITY_STYLES[card.rarity];
                return <button key={card.variant} onClick={() => card.unlocked && !isSelected && setSelectedVariant(card.variant)} className={`
                        relative w-14 h-14 rounded-full border-2 flex items-center justify-center overflow-hidden transition-all
                        ${card.unlocked ? `${styles.bg} ${styles.border} hover:scale-110` : 'bg-slate-50 border-slate-200 opacity-60 cursor-not-allowed'}
                        ${isSelected ? 'ring-4 ring-orange-400 ring-offset-2 scale-110' : ''}
                      `} title={card.name} data-id="element-1199">
                      {!card.unlocked && <div className="absolute top-1 right-1 z-20" data-id="element-1200">
                          <Lock className="w-3 h-3 text-slate-400 drop-shadow-sm" data-id="element-1201" />
                        </div>}

                      <div className={`transform scale-75 mt-2 ${!card.unlocked ? 'grayscale' : ''}`} data-id="element-1202">
                        {card.rarity === 'secret' && !card.unlocked ? <span className="text-sm font-black text-slate-400 mt-[-8px] block" data-id="element-1203">
                            ???
                          </span> : <DjayAvatar size="sm" emotion={card.unlocked ? isSelected ? 'excited' : 'happy' : 'sleeping'} variant={card.variant} animate={false} data-id="element-1204" />}
                      </div>
                    </button>;
              })}
              </div>
              <p className="text-sm font-bold text-slate-500 text-center sm:text-left" data-id="element-1205">
                {unlockedVariantsCount} van de 8 varianten ontgrendeld
              </p>
            </div>
          </div>
        </div>

        {/* Prestaties Section */}
        <div className="space-y-4" data-id="element-1206">
          <div className="flex items-center justify-between px-1" data-id="element-1207">
            <h3 className="text-xl font-black text-slate-800 flex items-center gap-2" data-id="element-1208">
              <Trophy className="w-6 h-6 text-amber-500" data-id="element-1209" /> Prestaties
            </h3>
          </div>

          {/* Category Tabs */}
          <div className="flex overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 hide-scrollbar gap-2" data-id="element-1210">
            <button onClick={() => setActiveTab('all')} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all ${activeTab === 'all' ? 'bg-slate-800 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-50 border-2 border-slate-100'}`} data-id="element-1211">
              Alle
            </button>
            {(Object.keys(CATEGORY_LABELS) as Achievement['category'][]).map(category => <button key={category} onClick={() => setActiveTab(category)} className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all ${activeTab === category ? 'bg-slate-800 text-white shadow-md' : 'bg-white text-slate-600 hover:bg-slate-50 border-2 border-slate-100'}`} data-id="element-1212">
                  {CATEGORY_LABELS[category]}
                </button>)}
          </div>

          {/* Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-1213">
            <AnimatePresence mode="popLayout" data-id="element-1214">
              {filteredAchievements.map((achievement, index) => {
              const Illustration = getAchievementIllustration(achievement.id);
              const variantName = achievement.djayVariantUnlocked ? mascotCards.find(c => c.variant === achievement.djayVariantUnlocked)?.name : null;
              return <motion.div key={achievement.id} layout initial={{
                opacity: 0,
                scale: 0.9
              }} animate={{
                opacity: 1,
                scale: 1
              }} exit={{
                opacity: 0,
                scale: 0.9
              }} transition={{
                duration: 0.2
              }} className="relative flex flex-col h-full" data-id="element-1215">
                    <div className="flex-1" data-id="element-1216">
                      {Illustration ? <CollectibleAchievementCard achievement={achievement} isUnlocked={achievement.isUnlocked} index={index} compact={false} data-id="element-1217" /> : <AchievementCard achievement={achievement} isUnlocked={achievement.isUnlocked} index={index} data-id="element-1218" />}
                    </div>

                    {/* Bonus Reward Pill */}
                    {achievement.djayVariantUnlocked && variantName && <div className={`mt-2 flex items-center gap-2 px-3 py-1.5 rounded-xl border-2 ${achievement.isUnlocked ? 'bg-orange-50 border-orange-200' : 'bg-slate-50 border-slate-200'}`} data-id="element-1219">
                        <div className={`w-8 h-8 flex-shrink-0 overflow-hidden rounded-full bg-white border border-slate-200 flex items-center justify-center ${!achievement.isUnlocked ? 'grayscale opacity-50' : ''}`} data-id="element-1220">
                          <div className="transform scale-75 mt-2" data-id="element-1221">
                            <DjayAvatar size="sm" emotion="happy" variant={achievement.djayVariantUnlocked} animate={false} data-id="element-1222" />
                          </div>
                        </div>
                        <div className="flex flex-col" data-id="element-1223">
                          <span className={`text-[9px] font-bold uppercase tracking-wider ${achievement.isUnlocked ? 'text-orange-500' : 'text-slate-400'}`} data-id="element-1224">
                            {achievement.isUnlocked ? 'Ontgrendeld:' : 'Beloning:'}
                          </span>
                          <span className={`text-xs font-black ${achievement.isUnlocked ? 'text-orange-900' : 'text-slate-500'}`} data-id="element-1225">
                            {variantName}
                          </span>
                        </div>
                      </div>}
                  </motion.div>;
            })}
            </AnimatePresence>
          </div>
        </div>
      </motion.div>
    </PageContainer>;
}